/**
 * PRODUCT-EXPERIENCE-CANON-AND-CLAIM-BOUNDARY-REMEDIATION-V0
 * Families A–D: identity · claim class · CTA/terminology · UI/mutation-result grammar.
 * Runtime mirror of docs/product/foundations/PRODUCT_*_V0.json — no authority grants.
 */

export const PRODUCT_EXPERIENCE_CANON_PROGRAM_ID_V0 =
  'PRODUCT-EXPERIENCE-CANON-AND-CLAIM-BOUNDARY-REMEDIATION-V0' as const;

export const PRODUCT_EXPERIENCE_CANON_CONTRACT_VERSION_V0 =
  'product-experience-canon-and-claim-boundary/v0' as const;

export const PRODUCT_IDENTITY_CANON_KO_V0 =
  'Inflomatrix는 현실의 업무를 구조화하여 인간과 AI가 같은 운영 흐름 위에서 협업하도록 만드는 기업 운영 OS다.' as const;

export const PRODUCT_IDENTITY_CANON_EN_V0 =
  'Inflomatrix is an enterprise operating OS that structures real-world work so humans and AI collaborate on the same operational flow.' as const;

export const PRODUCT_CLAIM_CLASSES_V0 = [
  'AVAILABLE',
  'PROVEN',
  'PREVIEW',
  'REQUIRES_CONFIGURATION',
  'PLANNED',
  'NOT_AVAILABLE',
  'UNSUPPORTED',
] as const;

export type ProductClaimClassV0 = (typeof PRODUCT_CLAIM_CLASSES_V0)[number];

export const PRODUCT_CLAIM_CLASS_LABELS_KO_V0: Record<ProductClaimClassV0, string> = {
  AVAILABLE: '사용 가능',
  PROVEN: '검증됨',
  PREVIEW: '미리보기',
  REQUIRES_CONFIGURATION: '설정 필요',
  PLANNED: '계획됨',
  NOT_AVAILABLE: '제공하지 않음',
  UNSUPPORTED: '지원하지 않음',
};

/** Legal/marketing version label after P0 GA downgrade. */
export const PRODUCT_ACCESS_VERSION_LABEL_V0 = 'Preview v0.1' as const;

export const PRODUCT_FORBIDDEN_USER_FACING_CLAIM_PHRASES_V0 = [
  'GA v1.0',
  'General Availability',
  'production-ready',
  'live pilot',
  'autonomous company',
  '자율 회사',
  '프로덕션 완성',
  '의식 진화',
  '살아있는 시스템',
] as const;

export const PRODUCT_BANNED_AMBIGUOUS_ENGLISH_CTAS_V0 = [
  'Run',
  'Submit',
  'Confirm',
  'Process',
  'Done',
] as const;

export const PRODUCT_PREFERRED_KOREAN_CTAS_V0 = {
  Run: '실행',
  Submit: '제출',
  Confirm: '확인',
  Process: '처리',
  Done: '완료',
  Assign: '담당 지정',
  'Start review': '검토 시작',
  'Request changes': '수정 요청',
  'Complete review': '검토 완료',
  Close: '닫기',
  Approve: '승인',
  Reject: '반려',
  'Submit Order': '주문 제출',
} as const;

export const PRODUCT_MUTATION_RESULT_ELEMENTS_V0 = [
  'action',
  'object',
  'outcome',
  'receipt',
  'next',
] as const;

export const PRODUCT_P0_STATE_BOUNDARY_INVARIANTS_V0 = [
  'SCAN_NE_MUTATION',
  'EVIDENCE_UPLOADED_NE_ACCEPTED',
  'APPROVAL_NE_EXECUTION_COMPLETE',
  'AI_PROPOSED_NE_ADMITTED',
  'EXECUTOR_SUCCESS_NE_RECONCILIATION',
] as const;

export type ProductExperienceCanonProofV0 = {
  program: typeof PRODUCT_EXPERIENCE_CANON_PROGRAM_ID_V0;
  contractVersion: typeof PRODUCT_EXPERIENCE_CANON_CONTRACT_VERSION_V0;
  proofLevel: 'LEVEL_4';
  ok: boolean;
  identityCanonKo: typeof PRODUCT_IDENTITY_CANON_KO_V0;
  claimClassCount: number;
  accessVersionLabel: typeof PRODUCT_ACCESS_VERSION_LABEL_V0;
  bannedEnglishCtaCount: number;
  mutationResultElementCount: number;
  p0BoundaryInvariantCount: number;
  productionMutation: 0;
  scenarios: Record<string, { ok: boolean }>;
  claims: {
    productionReadyClaimed: false;
    generalAvailabilityClaimed: false;
    pilotLiveClaimed: false;
  };
};

export function runProductExperienceCanonAndClaimBoundaryProofHarnessV0(): ProductExperienceCanonProofV0 {
  const scenarios = {
    identityCanon: { ok: PRODUCT_IDENTITY_CANON_KO_V0.includes('기업 운영 OS') },
    claimClasses: { ok: PRODUCT_CLAIM_CLASSES_V0.length === 7 },
    previewAccessLabel: { ok: PRODUCT_ACCESS_VERSION_LABEL_V0 === 'Preview v0.1' },
    bannedEnglishCtas: { ok: PRODUCT_BANNED_AMBIGUOUS_ENGLISH_CTAS_V0.length === 5 },
    mutationResultElements: { ok: PRODUCT_MUTATION_RESULT_ELEMENTS_V0.length === 5 },
    p0StateBoundaries: { ok: PRODUCT_P0_STATE_BOUNDARY_INVARIANTS_V0.length === 5 },
    gaPhraseForbidden: {
      ok: (PRODUCT_FORBIDDEN_USER_FACING_CLAIM_PHRASES_V0 as readonly string[]).includes('GA v1.0'),
    },
  };
  const ok = Object.values(scenarios).every((s) => s.ok);

  return {
    program: PRODUCT_EXPERIENCE_CANON_PROGRAM_ID_V0,
    contractVersion: PRODUCT_EXPERIENCE_CANON_CONTRACT_VERSION_V0,
    proofLevel: 'LEVEL_4',
    ok,
    identityCanonKo: PRODUCT_IDENTITY_CANON_KO_V0,
    claimClassCount: PRODUCT_CLAIM_CLASSES_V0.length,
    accessVersionLabel: PRODUCT_ACCESS_VERSION_LABEL_V0,
    bannedEnglishCtaCount: PRODUCT_BANNED_AMBIGUOUS_ENGLISH_CTAS_V0.length,
    mutationResultElementCount: PRODUCT_MUTATION_RESULT_ELEMENTS_V0.length,
    p0BoundaryInvariantCount: PRODUCT_P0_STATE_BOUNDARY_INVARIANTS_V0.length,
    productionMutation: 0,
    scenarios,
    claims: {
      productionReadyClaimed: false,
      generalAvailabilityClaimed: false,
      pilotLiveClaimed: false,
    },
  };
}
